import { useEffect, useRef } from 'react';
import { Helix2000Character } from '../../shared/CharacterTypes';
import { EventBus, EVENTS } from '../../shared/EventBus';
import { gameLogger } from '../../utils/GameLogger';
import { GameScene } from '../scenes/GameScene';

interface UseGameEventsOptions {
  player: Helix2000Character | null;
  setPlayer: React.Dispatch<React.SetStateAction<Helix2000Character | null>>;
  gameWorld: GameScene | null;
  setCurrentLevelName: (name: string) => void;
  setCurrentLevelId: (id: string) => void;
  handleDevAction: (action: string, data?: any) => void;
}

export function useGameEvents({
  setPlayer,
  gameWorld,
  setCurrentLevelName,
  setCurrentLevelId,
  handleDevAction
}: UseGameEventsOptions) {
  // `player` is intentionally NOT in the effect's deps array — the handlers
  // use functional setState (setPlayer(prev => …)), so the EventBus
  // subscriptions register once per gameWorld change, not once per render
  // (avoids sticky LEVEL_LOADED replay → setCurrentLevelName/Id log spam,
  // ~60Hz / ~280 suppressed-5s, DJ 2026-05-02).
  //
  // K15 (2026-07-17): the same trap re-entered through `handleDevAction` —
  // useDevActions rebuilds it whenever `player` changes, i.e. on EVERY
  // schema patch, so keeping it in the deps re-ran this effect (and the
  // sticky LEVEL_LOADED replay + its setStates) on every patch commit.
  // That scheduled an update during virtually every passive-effect flush,
  // which is one of the two feeders of the "Maximum update depth exceeded"
  // loop. Read it through a ref instead, same pattern as `player`.
  const handleDevActionRef = useRef(handleDevAction);
  handleDevActionRef.current = handleDevAction;

  useEffect(() => {
    if (!gameWorld) return;

    const handleMapLoaded = (data: { name: string, id?: string }) => {
      // Log removed 2026-05-02 — same spam path as handleTeleportSync.
      // The actual map-load event is logged once at the source
      // (MapSystem.loadLevel), which is the right place if you need it.
      setCurrentLevelName(data.name);
      if (data.id) {
        setCurrentLevelId(data.id);
        // Persist currentLevelId on the player for session restoration
        setPlayer(prev => {
          if (!prev) return prev;
          if ((prev as any).currentLevelId === data.id) return prev;
          return { ...prev, currentLevelId: data.id } as any;
        });
      }
    };

    const unsubMapLoaded = EventBus.getInstance().on(EVENTS.LEVEL_LOADED, handleMapLoaded);

    const handleShrineXP = (data: { amount: number }) => {
      gameLogger.info('game', `✨ Shrine granting ${data.amount} XP`);
      handleDevActionRef.current('addxp', data.amount);
    };

    const unsubShrine = EventBus.getInstance().on(EVENTS.XP_SHRINE_ACTIVATED, handleShrineXP);

    return () => {
      unsubMapLoaded();
      unsubShrine();
    };
    // `player` intentionally not in deps — read via playerRef so the
    // EventBus subscriptions register once per gameWorld change, not
    // once per render. setState identities (setPlayer / setCurrentLevelName
    // / setCurrentLevelId) are stable. `handleDevAction` is read through
    // handleDevActionRef (K15) — its identity churns with `player`, and
    // having it in deps re-ran this effect on every schema patch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameWorld]);
}
